import { defineStore, storeToRefs } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "@stores/useMyFetch.js";
import { deepCopy, areEqual } from "@utils/toolBox";
import handleError from "@composables/handleError.js";
import { useSurveyStore } from "@stores/survey/survey";
import { captureEvent } from "@/lib/analytics";

export const useSurveyCardsSettingsStore = defineStore("surveyCardsSettings", () => {
  // stores
  const surveyStore = useSurveyStore();
  const { survey } = storeToRefs(surveyStore);

  const cards = ref([]);
  const initialCards = ref([]);

  // getters
  const areSetupCardsAdminOnlyModel = computed(() => cards.value.map(({ is_admin }) => is_admin));
  const areSetupCardsHiddenInChartsModel = computed(() =>
    cards.value.map(({ is_charts }) => (is_charts ? !is_charts : true)),
  ); // also handles when undefined
  const haveSetupCardsChanged = computed(() => !areEqual(initialCards.value, cards.value));
  const changedCards = computed(() => {
    return cards.value.filter((card, index) => !areEqual(card, initialCards.value[index]));
  });

  async function setSetupCards() {
    try {
      let url = `/surveys/${survey.value.token}/cards`;
      const { data } = await useMyFetch(url).json();
      cards.value = data.value;
      initialCards.value = deepCopy(data.value);
    } catch (err) {
      handleError(err);
    }
  }

  async function saveSetupCards() {
    const saved = await Promise.all(
      changedCards.value.map(async (card) => {
        const { response } = await useMyFetch(`/surveys/${survey.value.token}/cards/${card.token}`)
          .patch({ card: { is_admin: card.is_admin, is_charts: card.is_charts } })
          .json();
        return response.value.ok;
      }),
    );

    if (saved.every(Boolean)) captureEvent("setup_cards_saved", { cards_count: saved.length });

    await setSetupCards();
  }

  const updateSetupCardAdminOnly = (index, value) => (cards.value[index].is_admin = value);
  const handleUpdateSetupCardAdminOnly = (index, value) => {
    updateSetupCardAdminOnly(index, value);
    if (value) updateSetupCardIsHiddenInChart(index, true);
  };
  const updateSetupCardIsHiddenInChart = (index, value) => (cards.value[index].is_charts = !value);

  return {
    // fetch
    setSetupCards,

    // getters
    cards,
    haveSetupCardsChanged,
    areSetupCardsAdminOnlyModel,
    areSetupCardsHiddenInChartsModel,

    // actions
    saveSetupCards,
    handleUpdateSetupCardAdminOnly,
    updateSetupCardIsHiddenInChart,
  };
});
